import { NotificationMetadata } from './common';

export type NotificationType = 
  | 'task'
  | 'guest'
  | 'budget'
  | 'system'
  | 'info'
  | 'warning'
  | 'success'
  | 'error';

export type NotificationCategory = 
  | 'tasks'
  | 'guests'
  | 'budget'
  | 'timeline'
  | 'vendors'
  | 'wedding-website'
  | 'achievements'
  | 'general';

export type NotificationStatus = 'pending' | 'sent' | 'failed' | 'clicked';
export type NotificationChannel = 'push' | 'email' | 'sms';
export type NotificationImportance = 'high' | 'medium' | 'low';

export interface Notification {
  id: string;
  userId: string;
  weddingId: string;
  title: string;
  message: string;
  type: NotificationType;
  category: NotificationCategory;
  isRead: boolean;
  createdAt: Date;
  link?: string;
  status?: NotificationStatus;
  channel?: NotificationChannel;
  importance?: NotificationImportance;
  metadata?: NotificationMetadata;
}

export interface NotificationCount {
  tasks?: number;
  guests?: number;
  budget?: number;
  timeline?: number;
  vendors?: number;
  'wedding-website'?: number;
  achievements?: number;
  general?: number;
}

// Type guards avec typage strict
export const isValidNotificationType = (type: unknown): type is NotificationType => {
  return typeof type === 'string' && [
    'task',
    'guest',
    'budget',
    'system',
    'info',
    'warning',
    'success',
    'error'
  ].includes(type);
};

export const isValidNotificationCategory = (category: unknown): category is NotificationCategory => {
  return typeof category === 'string' && [
    'tasks',
    'guests',
    'budget',
    'timeline',
    'vendors',
    'wedding-website',
    'achievements',
    'general'
  ].includes(category);
};

export const isValidNotificationStatus = (status: unknown): status is NotificationStatus => {
  return typeof status === 'string' && ['pending', 'sent', 'failed', 'clicked'].includes(status);
};

export const isValidNotificationChannel = (channel: unknown): channel is NotificationChannel => {
  return typeof channel === 'string' && ['push', 'email', 'sms'].includes(channel);
};

export const isValidNotificationImportance = (importance: unknown): importance is NotificationImportance => {
  return typeof importance === 'string' && ['high', 'medium', 'low'].includes(importance);
};

// Type pour la création de notifications
export type CreateNotificationData = Omit<Notification, 'id' | 'createdAt' | 'isRead'>;

// Type pour les préférences de notification
export interface NotificationPreferences {
  email: boolean;
  push: boolean;
  sms: boolean;
  categories: {
    tasks: boolean;
    guests: boolean;
    budget: boolean;
    timeline: boolean;
    vendors: boolean;
    'wedding-website': boolean;
    achievements: boolean;
    general: boolean;
  };
  quietHours?: {
    enabled: boolean;
    start: string; // Format HH:mm
    end: string;   // Format HH:mm
  };
}

// Type pour les paramètres de notification push
export interface PushNotificationData {
  title: string;
  body: string;
  data?: NotificationMetadata;
  icon?: string;
  badge?: string;
  tag?: string;
  requireInteraction?: boolean;
}

// Type pour les statistiques de notifications
export interface NotificationStats {
  total: number;
  unread: number;
  byCategory: NotificationCount;
  byType: Record<NotificationType, number>;
  recent: Notification[];
} 